// Route tester (/volunteer/routes, scope `routes`).
//
// Pick a Start and an End on the campus map (a nav node, or a room), and the
// route is computed right here in the browser with the same `findRoute` the
// /umanitoba pathfinder and the phone use. Every node on the path is drawn and
// named by type; three more variants (Stairs only, Accessible, Bad weather)
// can be overlaid in their own colours and compared side by side. Then the
// volunteer can make TEST EDITS to the graph — a node becomes blocked or
// non-accessible, a stair or elevator shaft is switched off — and watch the
// route re-plan around them.
//
// ⚠️ Edits never touch map data. They are translated into findRoute options
// (`editOptions` in routeTester/model.js) at the moment a route is computed
// and live in this session, the per-day history (server-side, /api/route-tests)
// and a "Suggest improvement" the operator reviews in /admin → Volunteer Admin
// Dashboard. Nothing here can change what a student is routed along.
//
// Drawing: the /umanitoba palette for structure polygons only (outline, room,
// stair, elevator, blocked, tunnel); one global floor 1–6 for every building,
// floor 1 also showing the tunnels (data floor −1); outdoor route parts always
// drawn, off-floor parts dimmed like /umanitoba's inactive legs. Nav nodes are
// NOT drawn wholesale (16k markers would bury the map): one appears under the
// cursor (nearest within ~14 px on the shown floor, hit-tested in world
// pixels), and the nodes along a drawn route are drawn as a legend-coloured
// circle each. Interiors are fetched lazily for buildings in view at zoom ≥ 16.
//
// Everything that needs the Maps script checks `hasMaps()` first — the page
// renders its toolbox without it, which is what the Jest suite looks at.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  createHttpDataSource, loadAllMapFiles, buildMapData, attachBuildingInterior, getRoomLocation,
  findRoute, shaftOf, buildDirections,
} from '../pathfinder/index.js';
import { api, ago, routeLink, readFileAsDataUrl, API_BASE } from './shared.js';
import {
  VARIANTS, VARIANT_BY_KEY, NODE_COLORS, LEGEND, START_COLOR, END_COLOR,
  editOptions, effectiveType, metricsOf, metricsSummary, nodeLabel, nodeEndpoint, roomEndpoint, roomEntry,
  endpointIds, endpointLabel, buildingName, resolveFromText, parseQuery, openInPathfinderLink,
  nodeEdit, shaftEdit, sameEdit, editCovering, editLabel, localShaftOf, visibleOnFloor, splitRuns,
  groupByDay, variantsText, isTypingTarget,
} from './routeTester/model.js';
import {
  hasMaps, POLY_COLORS, FOOTPRINT, ROOM_HOVER, pillIcon, nodeIcon, lineStyle, roomLabelClass,
  boundsOfRings, boxesIntersect, inBox, ringCentroid, pointInRing, boxOf,
} from './routeTester/mapKit.js';

// Same Maps key the rest of UMapsWeb uses (CRA inlines REACT_APP_* at build).
const MAPS_KEY = process.env.REACT_APP_MAPS_KEY || '';
const DS = createHttpDataSource('/Map%20Info/pathfinder/');
const FLOORS = [1, 2, 3, 4, 5, 6];
const HOVER_PX = 14;
const INTERIOR_ZOOM = 16;
const LABEL_ZOOM = 17;
const MAX_LABELS = 600;
const HISTORY_DEBOUNCE_MS = 1500;
const SHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const SHOT_MAX = 4 * 1024 * 1024;
const TOOL_HINT = {
  start: 'Click a node or a room to set the Start',
  end: 'Click a node or a room to set the End',
  blocked: 'Click a node to mark it Blocked',
  'non-accessible': 'Click a node to mark it Non-accessible',
  shaft: 'Click a stair or elevator node to disable its whole shaft',
};
const NO_VARIANTS = { stairs: false, accessible: false, weather: false };
const EMPTY_HIST = { edits: [], past: [], future: [] };

const resolveShaft = (mapData, id) => (typeof shaftOf === 'function' ? shaftOf : localShaftOf)(mapData, id);
const defaultFloor = (b) => b.floors.find((f) => f >= 1) ?? b.floors[0];
const modsOf = (e) => ({ shift: !!e?.domEvent?.shiftKey, alt: !!e?.domEvent?.altKey });

/**
 * World-pixel index of every node, per floor. World coordinates are
 * zoom-independent (the hit-test scales by 2^zoom), so it is built once per
 * mapData and never on zoom or idle. Cached on `ref`.
 */
function ensureWorldPts(ref, md, proj) {
  if (ref.current.md === md) return ref.current.byFloor;
  const byFloor = new Map();
  for (const n of md.nodes.values()) {
    if (!Number.isFinite(n.lat) || !Number.isFinite(n.lng)) continue;
    const q = proj.fromLatLngToPoint(new window.google.maps.LatLng(n.lat, n.lng));
    if (!byFloor.has(n.floor)) byFloor.set(n.floor, []);
    byFloor.get(n.floor).push({ id: n.id, x: q.x, y: q.y });
  }
  ref.current = { md, byFloor };
  return byFloor;
}

export default function RouteTester({ token, onExpired, admin }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY });

  // ── Data ───────────────────────────────────────────────────────────────────
  const [mapData, setMapData] = useState(null);
  const [dataErr, setDataErr] = useState('');
  const [interiorsVersion, setInteriorsVersion] = useState(0);
  useEffect(() => {
    let live = true;
    loadAllMapFiles(DS)
      .then((files) => { if (live) setMapData(buildMapData(files)); })
      .catch((e) => { if (live) setDataErr(e?.message || 'Could not load the campus map data.'); });
    return () => { live = false; };
  }, []);
  const roomsByKey = useMemo(() => {
    const m = new Map();
    for (const e of mapData?.roomIndex || []) if (e.building && e.roomNumber) m.set(`${e.building}::${String(e.roomNumber).toLowerCase()}`, e);
    return m;
  }, [mapData]);

  // ── Test state ─────────────────────────────────────────────────────────────
  const [floor, setFloor] = useState(1);
  const [showRooms, setShowRooms] = useState(false);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [variants, setVariants] = useState(NO_VARIANTS);
  const [hist, setHist] = useState(EMPTY_HIST);
  const edits = hist.edits;
  const [tool, setTool] = useState(null);
  const [hover, setHover] = useState(null);           // { node } | { room, rings } | null
  const [inspect, setInspect] = useState(null);       // node
  const [selectedEdit, setSelectedEdit] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState('base');
  const [tab, setTab] = useState('test');
  const [notice, setNotice] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyErr, setHistoryErr] = useState('');
  const [mine, setMine] = useState([]);
  const [mineErr, setMineErr] = useState('');
  const [mapReady, setMapReady] = useState(false);

  const noticeTimer = useRef(null);
  const say = useCallback((text) => {
    setNotice(text);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 3500);
  }, []);
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const commitEdits = useCallback((next) => {
    setHist((h) => ({ edits: next, past: [...h.past, h.edits].slice(-60), future: [] }));
    setSelectedEdit(null);
  }, []);
  const undo = useCallback(() => setHist((h) => (h.past.length
    ? { edits: h.past[h.past.length - 1], past: h.past.slice(0, -1), future: [h.edits, ...h.future] } : h)), []);
  const redo = useCallback(() => setHist((h) => (h.future.length
    ? { edits: h.future[0], past: [...h.past, h.edits], future: h.future.slice(1) } : h)), []);
  const addEdit = useCallback((edit) => {
    if (edits.some((e) => sameEdit(e, edit))) { say('That edit is already in the list.'); return; }
    commitEdits([...edits, edit]);
  }, [edits, commitEdits, say]);
  const removeEdit = useCallback((i) => commitEdits(edits.filter((_, k) => k !== i)), [edits, commitEdits]);

  /** Load a complete Params (history row, suggestion) into the tester. */
  const loadParams = useCallback((p, editsOverride) => {
    if (!p) return;
    setStart(p.start || null);
    setEnd(p.end || null);
    setVariants({ ...NO_VARIANTS, ...(p.variants || {}) });
    setHist({ edits: editsOverride || p.edits || [], past: [], future: [] });
    setSelectedEdit(null);
    setTool(null);
    setTab('test');
  }, []);

  // ── Routing (edits → options, never → data) ────────────────────────────────
  const results = useMemo(() => {
    if (!mapData || !start || !end) return null;
    const fromIds = endpointIds(mapData, start), toIds = endpointIds(mapData, end);
    if (!fromIds.length || !toIds.length) return { routes: {}, metrics: {}, before: {}, unresolved: true };
    const extra = editOptions(edits);
    const run = (v, withEdits) => findRoute(mapData, fromIds, toIds, withEdits ? { ...v.options, ...extra } : { ...v.options });
    const routes = {}, metrics = {}, before = {};
    for (const v of VARIANTS) {
      if (v.key !== 'base' && !variants[v.key]) continue;
      routes[v.key] = run(v, true);
      metrics[v.key] = metricsOf(routes[v.key]);
      before[v.key] = edits.length ? metricsOf(run(v, false)) : metrics[v.key];
    }
    return { routes, metrics, before, unresolved: false };
  }, [mapData, start, end, variants, edits]);
  const ranKeys = useMemo(() => VARIANTS.map((v) => v.key).filter((k) => results?.routes && k in results.routes), [results]);
  const shownVariant = ranKeys.includes(selectedVariant) ? selectedVariant : 'base';
  const shownRoute = results?.routes?.[shownVariant] || null;
  const directions = useMemo(() => (shownRoute && mapData ? buildDirections(shownRoute, mapData) : []), [shownRoute, mapData]);

  // ── Server: history + my suggestions ───────────────────────────────────────
  const loadHistory = useCallback(async () => {
    const r = await api('/api/route-tests?part=history', { token, onExpired });
    if (r.ok) { setHistory(r.body.entries || []); setHistoryErr(''); }
    else setHistoryErr(r.body.error || 'Could not load your history.');
  }, [token, onExpired]);
  const loadMine = useCallback(async () => {
    const r = await api('/api/route-tests?part=mine', { token, onExpired });
    if (r.ok) { setMine(r.body.suggestions || []); setMineErr(''); }
    else setMineErr(r.body.error || 'Could not load your suggestions.');
  }, [token, onExpired]);
  useEffect(() => { loadHistory(); loadMine(); }, [loadHistory, loadMine]);

  // Whenever a result settles, it goes into today's history (debounced; the
  // server folds a repeat of the same params into the existing row).
  useEffect(() => {
    if (!results || results.unresolved || !start || !end) return undefined;
    const t = setTimeout(async () => {
      const r = await api('/api/route-tests', {
        method: 'POST', token, onExpired,
        body: { op: 'history-add', params: { start, end, variants, edits }, metrics: results.metrics },
      });
      if (r.ok && r.body.entry) {
        const e = r.body.entry;
        setHistory((h) => [e, ...h.filter((x) => x.id !== e.id && (!e.sig || x.sig !== e.sig))]);
      }
    }, HISTORY_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [results, start, end, variants, edits, token, onExpired]);

  // ── Query params, once the data is here ────────────────────────────────────
  const queryRef = useRef(parseQuery(typeof window !== 'undefined' ? window.location.search : ''));
  useEffect(() => {
    const q = queryRef.current;
    if (!mapData || !q) return;
    queryRef.current = null;
    if (q.suggestion) {
      (async () => {
        const r = await api(`/api/route-tests?part=suggestion&id=${encodeURIComponent(q.suggestion)}`, { token, onExpired });
        const s = r.ok && r.body.suggestion;
        if (!s) { say('That suggestion could not be loaded.'); return; }
        loadParams(s.original, s.edits || []);
        setSelectedVariant('base');
        say(`Loaded suggestion from ${s.byName || s.by || 'a volunteer'} with its edits applied.`);
      })();
      return;
    }
    const from = q.from ? resolveFromText(mapData, q.from) : null;
    const to = q.b && q.r ? roomEntry(mapData, q.b, q.r) : null;
    if (from) setStart(from);
    if (to) setEnd(roomEndpoint(to));
    if (q.variants.stairs || q.variants.accessible || q.variants.weather) setVariants({ ...NO_VARIANTS, ...q.variants });
    if (q.from && !from) say(`Could not find "${q.from}" as a start.`);
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
  }, [mapData, token, onExpired, loadParams, say]);

  // ── Picking ────────────────────────────────────────────────────────────────
  const handleNode = useCallback((node, mods = {}) => {
    if (!node) return;
    if (mods.alt) { setStart(nodeEndpoint(node)); setTool(null); return; }
    if (mods.shift) { setEnd(nodeEndpoint(node)); setTool(null); return; }
    if (tool === 'start') { setStart(nodeEndpoint(node)); setTool(null); return; }
    if (tool === 'end') { setEnd(nodeEndpoint(node)); setTool(null); return; }
    if (tool === 'blocked' || tool === 'non-accessible') {
      const i = editCovering(edits, node.id);
      if (i >= 0) { say(`${nodeLabel(node)} already has an edit — remove it first.`); return; }
      addEdit(nodeEdit(node, tool)); setTool(null); return;
    }
    if (tool === 'shaft') {
      const s = mapData ? resolveShaft(mapData, node.id) : null;
      if (!s) { say('That is not a stair or elevator node.'); return; }
      addEdit(shaftEdit(node, s)); setTool(null); return;
    }
    setInspect(node);
  }, [tool, edits, addEdit, mapData, say]);
  const handleRoom = useCallback((entry) => {
    if (!entry) return;
    if (tool === 'start') { setStart(roomEndpoint(entry)); setTool(null); }
    else if (tool === 'end') { setEnd(roomEndpoint(entry)); setTool(null); }
  }, [tool]);
  const swap = useCallback(() => { setStart(end); setEnd(start); }, [start, end]);
  const toggleVariant = useCallback((k) => setVariants((v) => ({ ...v, [k]: !v[k] })), []);

  // ── Map bootstrap ──────────────────────────────────────────────────────────
  const mapDiv = useRef(null);
  const mapObj = useRef(null);
  const latest = useRef({});
  latest.current = { mapData, floor, tool, hover, showRooms, handleNode, handleRoom, roomsByKey };

  const fitTo = useCallback((nodesOrRings) => {
    const map = mapObj.current;
    if (!map || !hasMaps()) return;
    const b = new window.google.maps.LatLngBounds();
    let any = false;
    for (const p of nodesOrRings) { if (p && Number.isFinite(p.lat)) { b.extend({ lat: p.lat, lng: p.lng }); any = true; } }
    if (any) map.fitBounds(b, 60);
  }, []);
  const fitRoute = useCallback(() => {
    if (shownRoute) fitTo(shownRoute.nodes); else say('No route to fit yet.');
  }, [shownRoute, fitTo, say]);
  // A NEW pair of endpoints fits the map to its route once — a link from
  // Issues, a history row or a suggestion would otherwise land on the whole
  // campus, below the zoom at which interiors draw. Edits and variant toggles
  // keep the same endpoints, so they never move the map under the cursor.
  const fittedKey = useRef('');
  useEffect(() => {
    // Waits for the map: a route loaded from a link is usually computed
    // before the Maps script has produced a map object to fit.
    if (!mapReady) return;
    const key = shownRoute ? `${shownRoute.fromId}|${shownRoute.toId}` : '';
    if (!key || key === fittedKey.current) return;
    fittedKey.current = key;
    fitTo(shownRoute.nodes);
  }, [shownRoute, fitTo, mapReady]);

  // World-pixel index of the nodes, per floor (see ensureWorldPts).
  const worldPts = useRef({ md: null, byFloor: new Map() });
  // Rooms on the shown floor, for hit-testing under the cursor.
  const floorRooms = useRef([]);
  useEffect(() => {
    const out = [];
    for (const b of mapData?.buildings || []) {
      if (b.outlineOnly) continue;
      for (const p of b.polygonsByFloor.get(floor) || []) {
        if (p.type !== 'room' || !p.roomNumber) continue;
        const entry = roomsByKey.get(`${b.slug}::${String(p.roomNumber).toLowerCase()}`);
        if (!entry) continue;
        out.push({ entry, ring: p.ring, box: boundsOfRings([p.ring]) });
      }
    }
    floorRooms.current = out;
  }, [mapData, interiorsVersion, floor, roomsByKey]);

  const interiorReq = useRef(new Set());
  const outlineBox = useRef(new Map());
  const requestInteriors = useCallback((slugs) => {
    const md = latest.current.mapData;
    const pending = slugs.filter((s) => !interiorReq.current.has(s));
    if (!md || !pending.length) return;
    pending.forEach((s) => interiorReq.current.add(s));
    Promise.all(pending.map((slug) => DS.loadBuilding(slug)
      .then((j) => !!(j && attachBuildingInterior(md, slug, j)))
      .catch(() => { interiorReq.current.delete(slug); return false; })))
      .then((res) => { if (res.some(Boolean)) setInteriorsVersion((v) => v + 1); });
  }, []);
  const loadVisibleInteriors = useCallback(() => {
    const map = mapObj.current, md = latest.current.mapData;
    if (!map || !md || (map.getZoom() || 0) < INTERIOR_ZOOM) return;
    const box = boxOf(map.getBounds());
    if (!box) return;
    const want = [];
    for (const b of md.buildings) {
      if (!b.outlineOnly || interiorReq.current.has(b.slug)) continue;
      let bb = outlineBox.current.get(b.slug);
      if (bb === undefined) { bb = boundsOfRings(b.outlines); outlineBox.current.set(b.slug, bb); }
      if (boxesIntersect(bb, box)) want.push(b.slug);
    }
    requestInteriors(want);
  }, [requestInteriors]);
  // The tunnels have no footprint to intersect; they come in with the data.
  useEffect(() => {
    if (mapData?.buildings.some((b) => b.slug === 'tunnels' && b.outlineOnly)) requestInteriors(['tunnels']);
  }, [mapData, requestInteriors]);

  // Room-number labels: only on idle, only the shown floor, only in view, only
  // from zoom 17. Rebuilt whole each time — they are cheap divs.
  const labelObjs = useRef([]);
  const rebuildLabels = useCallback(() => {
    const map = mapObj.current, md = latest.current.mapData;
    labelObjs.current.forEach((l) => l.setMap(null));
    labelObjs.current = [];
    if (!map || !md || !latest.current.showRooms) return;
    const zoom = map.getZoom() || 0;
    if (zoom < LABEL_ZOOM) return;
    const Label = roomLabelClass();
    const box = boxOf(map.getBounds());
    if (!Label || !box) return;
    const f = latest.current.floor;
    let n = 0;
    for (const b of md.buildings) {
      if (b.outlineOnly) continue;
      for (const p of b.polygonsByFloor.get(f) || []) {
        if (p.type !== 'room' || !p.roomNumber) continue;
        const c = ringCentroid(p.ring);
        if (!c || !inBox(c, box)) continue;
        const l = new Label(new window.google.maps.LatLng(c.lat, c.lng), p.roomNumber, zoom);
        l.setMap(map);
        labelObjs.current.push(l);
        if (++n >= MAX_LABELS) return;
      }
    }
  }, []);

  useEffect(() => {
    if (!isLoaded || !hasMaps() || !mapDiv.current || mapObj.current) return undefined;
    const g = window.google.maps;
    const map = new g.Map(mapDiv.current, {
      center: { lat: 49.8075, lng: -97.1370 }, zoom: 15,
      mapTypeId: 'satellite', tilt: 0, heading: 0, rotateControl: false,
      mapTypeControl: false, streetViewControl: false, fullscreenControl: false, zoomControl: false,
      clickableIcons: false, gestureHandling: 'greedy', keyboardShortcuts: false,
    });
    mapObj.current = map;
    const L = latest;
    let raf = 0, pendingLatLng = null;
    const hitTest = () => {
      raf = 0;
      const latLng = pendingLatLng;
      const md = L.current.mapData;
      const proj = map.getProjection();
      if (!latLng || !md || !proj) return;
      const byFloor = ensureWorldPts(worldPts, md, proj);
      const scale = 2 ** (map.getZoom() || 0);
      const p = proj.fromLatLngToPoint(latLng);
      const f = L.current.floor;
      const floors = f === 1 ? [f, 0, -1] : [f, 0];
      let best = null, bestD = HOVER_PX;
      for (const fl of floors) {
        for (const q of byFloor.get(fl) || []) {
          const d = Math.hypot((q.x - p.x) * scale, (q.y - p.y) * scale);
          if (d < bestD) { bestD = d; best = q.id; }
        }
      }
      const prev = L.current.hover;
      if (best) {
        if (prev?.node?.id !== best) setHover({ node: md.nodes.get(best) });
        return;
      }
      const t = L.current.tool;
      if (t === 'start' || t === 'end') {
        const pt = { lat: latLng.lat(), lng: latLng.lng() };
        for (const r of floorRooms.current) {
          if (!inBox(pt, r.box) || !pointInRing(pt, r.ring)) continue;
          if (prev?.room !== r.entry) setHover({ room: r.entry, rings: [r.ring] });
          return;
        }
      }
      if (prev) setHover(null);
    };
    const ls = [
      map.addListener('mousemove', (e) => {
        if (!e?.latLng) return;
        pendingLatLng = e.latLng;
        if (!raf) raf = window.requestAnimationFrame(hitTest);
      }),
      map.addListener('mouseout', () => { pendingLatLng = null; if (L.current.hover) setHover(null); }),
      map.addListener('click', (e) => {
        const h = L.current.hover;
        if (h?.node) L.current.handleNode(h.node, modsOf(e));
        else if (h?.room) L.current.handleRoom(h.room);
        else if (!L.current.tool) setInspect(null);
      }),
      map.addListener('idle', () => { loadVisibleInteriors(); rebuildLabels(); }),
      map.addListener('zoom_changed', () => { const z = map.getZoom(); labelObjs.current.forEach((l) => l.applyZoom(z)); }),
    ];
    setMapReady(true);
    return () => {
      ls.forEach((l) => g.event.removeListener(l));
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [isLoaded, loadVisibleInteriors, rebuildLabels]);

  // Initial view: every footprint.
  const didFit = useRef(false);
  useEffect(() => {
    if (!mapReady || !mapData || didFit.current) return;
    didFit.current = true;
    // A route that arrived with the page (a link from Issues, a suggestion)
    // has already fitted the map to itself; the campus view would undo that.
    if (fittedKey.current) return;
    const pts = [];
    for (const b of mapData.buildings) for (const ring of b.outlines) pts.push(...ring);
    if (!pts.length) for (const n of mapData.nodes.values()) pts.push(n);
    fitTo(pts);
  }, [mapReady, mapData, fitTo]);

  useEffect(() => { if (mapReady) rebuildLabels(); }, [mapReady, showRooms, floor, interiorsVersion, rebuildLabels]);
  useEffect(() => { mapObj.current?.setOptions({ draggableCursor: tool ? 'crosshair' : null }); }, [tool, mapReady]);

  // ── Polygons: built per building when its set changes, shown per floor ────
  const polyObjs = useRef(new Map()); // slug → { built, byFloor: Map<floor, Polygon[]> }
  useEffect(() => {
    if (!mapReady || !mapData || !hasMaps()) return;
    const g = window.google.maps;
    const seen = new Set();
    for (const b of mapData.buildings) {
      seen.add(b.slug);
      const cur = polyObjs.current.get(b.slug);
      if (cur && cur.built === b.polygonsByFloor) continue;
      if (cur) for (const polys of cur.byFloor.values()) polys.forEach((p) => p.setMap(null));
      const byFloor = new Map();
      for (const [f, polys] of b.polygonsByFloor) {
        const list = [];
        for (const p of polys) {
          const st = POLY_COLORS[p.type];
          if (!st) continue; // structure only
          list.push(new g.Polygon({
            paths: p.ring, fillColor: st.fill, fillOpacity: st.fillOp, strokeColor: st.stroke, strokeWeight: st.strokeW,
            clickable: false, zIndex: p.type === 'outline' ? 10 : 20, map: null,
            rtOutline: p.type === 'outline', rtBlue: false,
          }));
        }
        byFloor.set(f, list);
      }
      polyObjs.current.set(b.slug, { built: b.polygonsByFloor, byFloor });
    }
    for (const slug of [...polyObjs.current.keys()]) {
      if (seen.has(slug)) continue;
      for (const polys of polyObjs.current.get(slug).byFloor.values()) polys.forEach((p) => p.setMap(null));
      polyObjs.current.delete(slug);
    }
  }, [mapReady, mapData, interiorsVersion]);
  useEffect(() => {
    if (!mapReady || !mapData) return;
    const map = mapObj.current;
    const setBlue = (p, blue) => {
      if (p.get('rtBlue') === blue) return;
      p.set('rtBlue', blue);
      const st = blue ? FOOTPRINT : POLY_COLORS.outline;
      p.setOptions({ fillColor: st.fill, fillOpacity: st.fillOp, strokeColor: st.stroke, strokeWeight: st.strokeW, strokeOpacity: blue ? 0.9 : 1 });
    };
    for (const b of mapData.buildings) {
      const objs = polyObjs.current.get(b.slug);
      if (!objs) continue;
      if (b.slug === 'tunnels') {
        for (const [f, polys] of objs.byFloor) polys.forEach((p) => p.setMap(f === -1 && floor === 1 ? map : null));
        continue;
      }
      const hasFloor = !b.outlineOnly && b.polygonsByFloor.has(floor);
      const show = hasFloor ? floor : defaultFloor(b);
      for (const [f, polys] of objs.byFloor) {
        for (const p of polys) {
          if (f !== show) { p.setMap(null); continue; }
          const outline = p.get('rtOutline');
          if (outline) setBlue(p, !hasFloor);
          p.setMap(outline || hasFloor ? map : null);
        }
      }
    }
  }, [mapReady, mapData, interiorsVersion, floor]);
  useEffect(() => () => {
    for (const o of polyObjs.current.values()) for (const polys of o.byFloor.values()) polys.forEach((p) => p.setMap(null));
    polyObjs.current.clear();
    labelObjs.current.forEach((l) => l.setMap(null));
    labelObjs.current = [];
  }, []);

  // ── Route lines ────────────────────────────────────────────────────────────
  const lineObjs = useRef([]);
  useEffect(() => {
    if (!mapReady || !hasMaps()) return undefined;
    const map = mapObj.current;
    const clear = () => { lineObjs.current.forEach((l) => l.setMap(null)); lineObjs.current = []; };
    clear();
    if (!results?.routes) return clear;
    VARIANTS.forEach((v, i) => {
      const route = results.routes[v.key];
      if (!route) return;
      const raised = v.key === shownVariant;
      for (const run of splitRuns(route, floor)) {
        const weight = run.visible ? v.weight : 3;
        const opacity = run.visible ? 0.95 : 0.25;
        lineObjs.current.push(new window.google.maps.Polyline({
          path: run.path, map, clickable: false,
          strokeColor: v.color, strokeWeight: weight,
          zIndex: (raised ? 520 : 500) + i,
          ...lineStyle(v.dash, v.color, weight, opacity),
        }));
      }
    });
    return clear;
  }, [mapReady, results, floor, shownVariant]);

  // ── Nodes along the routes + edited nodes, coloured by type ───────────────
  const nodeObjs = useRef([]);
  useEffect(() => {
    if (!mapReady || !hasMaps() || !mapData) return undefined;
    const map = mapObj.current;
    const g = window.google.maps;
    const clear = () => { nodeObjs.current.forEach((m) => m.setMap(null)); nodeObjs.current = []; };
    clear();
    const wanted = new Map();
    for (const k of Object.keys(results?.routes || {})) for (const n of results.routes[k]?.nodes || []) wanted.set(n.id, n);
    for (const e of edits) {
      if (e.kind === 'node') { const n = mapData.nodes.get(e.id); if (n) wanted.set(n.id, n); }
      else for (const id of e.nodeIds || []) { const n = mapData.nodes.get(id); if (n) wanted.set(n.id, n); }
    }
    for (const n of wanted.values()) {
      const t = effectiveType(n, edits);
      const vis = visibleOnFloor(n, floor);
      const opacity = vis ? 1 : 0.3;
      const icon = t === 'blocked' ? nodeIcon(NODE_COLORS.blocked, { mark: '✕', opacity })
        : t === 'non-accessible' ? nodeIcon(NODE_COLORS['non-accessible'], { mark: '⚠', opacity })
        : t === 'shaftOff' ? nodeIcon(NODE_COLORS.shaftOff, { hatched: true, opacity })
        : nodeIcon(NODE_COLORS[t] || NODE_COLORS.waypoint, { opacity });
      const m = new g.Marker({
        position: { lat: n.lat, lng: n.lng }, map, icon, zIndex: vis ? 650 : 610, optimized: false,
        title: `${nodeLabel(n)} · ${t} · ${n.floor === 0 ? 'outdoors' : `${buildingName(mapData, n.building)} F${n.floor}`}`,
      });
      m.addListener('click', (e) => latest.current.handleNode(n, modsOf(e)));
      nodeObjs.current.push(m);
    }
    return clear;
  }, [mapReady, mapData, results, edits, floor]);

  // ── Start / End pills and the chosen rooms ─────────────────────────────────
  const endObjs = useRef([]);
  useEffect(() => {
    if (!mapReady || !hasMaps() || !mapData) return undefined;
    const map = mapObj.current;
    const g = window.google.maps;
    const clear = () => { endObjs.current.forEach((o) => o.setMap(null)); endObjs.current = []; };
    clear();
    const put = (ep, color, fallback) => {
      if (!ep) return;
      let pos = null;
      if (ep.kind === 'node') { const n = mapData.nodes.get(ep.id); if (n) pos = { lat: n.lat, lng: n.lng }; }
      else {
        const entry = roomEntry(mapData, ep.building, ep.room);
        const loc = entry ? getRoomLocation(mapData, entry) : null;
        pos = loc?.position || null;
        if (loc?.rings?.length && loc.floor === floor) {
          endObjs.current.push(new g.Polygon({ paths: loc.rings, map, clickable: false, zIndex: 30,
            fillColor: color, fillOpacity: 0.35, strokeColor: color, strokeWeight: 2 }));
        }
      }
      if (!pos) return;
      endObjs.current.push(new g.Marker({ position: pos, map, zIndex: 700, icon: pillIcon(ep.label || fallback, color), title: ep.label || fallback }));
    };
    put(start, START_COLOR, 'Start');
    put(end, END_COLOR, 'End');
    return clear;
  }, [mapReady, mapData, start, end, floor, interiorsVersion]);

  // ── Hover: one node marker, or a room highlight with its door nodes ───────
  const hoverObjs = useRef([]);
  useEffect(() => {
    if (!mapReady || !hasMaps() || !mapData) return undefined;
    const map = mapObj.current;
    const g = window.google.maps;
    const clear = () => { hoverObjs.current.forEach((o) => o.setMap(null)); hoverObjs.current = []; };
    clear();
    if (!hover) return clear;
    if (hover.node) {
      const n = hover.node;
      hoverObjs.current.push(new g.Marker({
        position: { lat: n.lat, lng: n.lng }, map, zIndex: 800, clickable: false,
        icon: nodeIcon(NODE_COLORS[effectiveType(n, edits)] || NODE_COLORS.waypoint, { scale: 7, ring: '#fff' }),
      }));
    } else if (hover.room) {
      hoverObjs.current.push(new g.Polygon({ paths: hover.rings, map, clickable: false, zIndex: 40,
        fillColor: ROOM_HOVER.fill, fillOpacity: ROOM_HOVER.fillOp, strokeColor: ROOM_HOVER.stroke, strokeWeight: ROOM_HOVER.strokeW }));
      for (const id of hover.room.nodeIds || []) {
        const n = mapData.nodes.get(id);
        if (!n) continue;
        hoverObjs.current.push(new g.Marker({ position: { lat: n.lat, lng: n.lng }, map, zIndex: 800, clickable: false,
          icon: nodeIcon(NODE_COLORS.door, { scale: 6 }), title: `Door ${nodeLabel(n)}` }));
      }
    }
    return clear;
  }, [mapReady, mapData, hover, edits]);

  // ── Keyboard ───────────────────────────────────────────────────────────────
  const keys = useRef({});
  keys.current = {
    hover, tool, results, start, end, selectedEdit, edits, suggestOpen, helpOpen,
    setTool, setStart, setEnd, swap, undo, redo, setFloor, setShowRooms, toggleVariant, fitRoute, setTab,
    setHelpOpen, setSuggestOpen, removeEdit, handleNode, handleRoom, setSelectedEdit, setInspect,
  };
  useEffect(() => {
    const onKey = (e) => {
      if (isTypingTarget(e.target)) return;
      const k = keys.current;
      const key = e.key;
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (key === 'z' || key === 'Z')) { e.preventDefault(); if (e.shiftKey) k.redo(); else k.undo(); return; }
      if (meta || e.altKey) return;
      if (key === 'Escape') {
        if (k.helpOpen) k.setHelpOpen(false);
        else if (k.suggestOpen) k.setSuggestOpen(false);
        else if (k.tool) k.setTool(null);
        else { k.setInspect(null); k.setSelectedEdit(null); }
        return;
      }
      if (k.helpOpen || k.suggestOpen) return;
      if (key === '?') { e.preventDefault(); k.setHelpOpen(true); return; }
      if (key === 'Enter') {
        if (k.tool && k.hover?.node) k.handleNode(k.hover.node);
        else if (k.tool && k.hover?.room) k.handleRoom(k.hover.room);
        else if (!k.tool && k.results?.routes?.base !== undefined) k.setSuggestOpen(true);
        return;
      }
      if (key === 'Backspace' || key === 'Delete') {
        e.preventDefault();
        if (k.selectedEdit != null && k.edits[k.selectedEdit]) k.removeEdit(k.selectedEdit);
        else if (k.end) k.setEnd(null);
        else if (k.start) k.setStart(null);
        return;
      }
      if (/^[1-6]$/.test(key)) { k.setFloor(Number(key)); return; }
      const arm = (t) => k.setTool((cur) => (cur === t ? null : t));
      switch (key.toLowerCase()) {
        case 's': arm('start'); break;
        case 'e': arm('end'); break;
        case 'x': k.swap(); break;
        case 'b': arm('blocked'); break;
        case 'n': arm('non-accessible'); break;
        case 'd': arm('shaft'); break;
        case 'r': k.setShowRooms((v) => !v); break;
        case 'a': k.toggleVariant('accessible'); break;
        case 'w': k.toggleVariant('weather'); break;
        case 'q': k.toggleVariant('stairs'); break;
        case 'z': k.fitRoute(); break;
        case 'h': k.setTab((t) => (t === 'history' ? 'test' : 'history')); break;
        default: return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Derived bits for the panel ─────────────────────────────────────────────
  const openLink = useMemo(() => (mapData ? openInPathfinderLink(mapData, start, end, variants, routeLink) : null), [mapData, start, end, variants]);
  const disconnectedRooms = useMemo(() => (mapData?.roomIndex || []).filter((e) => e.connected === false && e.roomNumber && !e.parkingKey && !e.bikeParking), [mapData]);
  const hasRoute = !!results && !results.unresolved;
  const goToRoom = useCallback((entry) => {
    const loc = getRoomLocation(mapData, entry);
    if (!loc?.position) return;
    if (loc.floor >= 1) setFloor(loc.floor);
    const map = mapObj.current;
    if (map) { map.panTo(loc.position); if ((map.getZoom() || 0) < 19) map.setZoom(19); }
  }, [mapData]);

  const deleteHistory = useCallback(async (id) => {
    const r = await api('/api/route-tests', { method: 'POST', token, onExpired, body: { op: 'history-delete', id } });
    if (r.ok) setHistory((h) => h.filter((x) => x.id !== id)); else say(r.body.error || 'Could not delete that.');
  }, [token, onExpired, say]);
  const clearHistory = useCallback(async () => {
    if (!window.confirm('Clear your whole test history?')) return;
    const r = await api('/api/route-tests', { method: 'POST', token, onExpired, body: { op: 'history-clear' } });
    if (r.ok) setHistory([]); else say(r.body.error || 'Could not clear the history.');
  }, [token, onExpired, say]);

  const endpointRow = (which, ep, color, toolKey) => (
    <div className="rt-ep">
      <button type="button" className={`vt-btn vt-small${tool === toolKey ? ' vt-primary' : ''}`}
              aria-pressed={tool === toolKey} onClick={() => setTool((t) => (t === toolKey ? null : toolKey))}>
        <span className="rt-dot" style={{ background: color }} aria-hidden="true" />{which}
      </button>
      <span className="rt-eplabel" title={ep ? endpointLabel(mapData, ep) : ''}>
        {ep ? endpointLabel(mapData, ep) : <span className="rt-faint">not set</span>}
      </span>
      {ep && (
        <button type="button" className="vt-btn vt-ghost vt-small" aria-label={`Clear ${which}`}
                onClick={() => (which === 'Start' ? setStart(null) : setEnd(null))}>✕</button>
      )}
    </div>
  );

  return (
    <div className="rt">
      <style>{CSS}</style>
      <aside className="rt-panel">
        <div className="rt-tabs vt-tabs" role="group" aria-label="Route tester">
          <button type="button" className="vt-tab" aria-pressed={tab === 'test'} onClick={() => setTab('test')}>Test</button>
          <button type="button" className="vt-tab" aria-pressed={tab === 'history'} onClick={() => setTab('history')}>
            History{history.length ? <span className="vt-count">{history.length}</span> : null}
          </button>
          <button type="button" className="vt-tab" aria-pressed={tab === 'suggest'} onClick={() => setTab('suggest')}>
            Suggestions{mine.length ? <span className="vt-count">{mine.length}</span> : null}
          </button>
          <button type="button" className="vt-btn vt-ghost vt-small rt-help" onClick={() => setHelpOpen(true)} aria-label="Keyboard shortcuts" title="Keyboard shortcuts (?)">?</button>
        </div>
        {dataErr && <div className="vt-err">{dataErr}</div>}
        {!dataErr && !mapData && <div className="vt-hint">Loading the campus map…</div>}
        {!MAPS_KEY && <div className="vt-note" style={{ marginBottom: 10 }}>No Google Maps key is configured (REACT_APP_MAPS_KEY) — the map cannot draw, but routes from a loaded history row still compute.</div>}
        {notice && <div className="vt-ok" role="status">{notice}</div>}

        {tab === 'test' && (
          <div className="rt-test">
            <section className="rt-sec">
              <h2 className="rt-h">Points</h2>
              {endpointRow('Start', start, START_COLOR, 'start')}
              {endpointRow('End', end, END_COLOR, 'end')}
              <div className="rt-row">
                <button type="button" className="vt-btn vt-small" onClick={swap} disabled={!start && !end} title="Swap (X)">⇄ Swap</button>
                <button type="button" className="vt-btn vt-small" onClick={fitRoute} disabled={!shownRoute} title="Fit map to route (Z)">Fit route</button>
                {openLink && <a className="vt-btn vt-small" href={openLink} target="_blank" rel="noopener noreferrer">Open in /umanitoba ↗</a>}
              </div>
              <p className="vt-hint">
                {tool ? <><b>{TOOL_HINT[tool]}</b> · Esc cancels</>
                  : 'Hover the map to reveal the nearest node; Alt+click sets Start, Shift+click sets End. Press ? for every shortcut.'}
              </p>
              {results?.unresolved && <div className="vt-err">One of the points has no routable node (a room with no door in the graph).</div>}
            </section>

            <section className="rt-sec">
              <h2 className="rt-h">Variants</h2>
              <div className="rt-variants">
                {VARIANTS.map((v) => (
                  <label key={v.key} className={`rt-variant${v.key === 'base' ? ' rt-variant-fixed' : ''}`}>
                    <input type="checkbox" checked={v.key === 'base' ? true : !!variants[v.key]} disabled={v.key === 'base'}
                           onChange={() => toggleVariant(v.key)} />
                    <span className={`rt-swatch rt-swatch-${v.dash}`} style={{ color: v.color }} aria-hidden="true" />
                    {v.label}
                  </label>
                ))}
              </div>
            </section>

            <section className="rt-sec">
              <h2 className="rt-h">Results</h2>
              {!start || !end ? <p className="vt-hint">Set a Start and an End — the Normal route runs by itself.</p>
                : !hasRoute ? null
                : <Analytics results={results} ranKeys={ranKeys} shown={shownVariant} onPick={setSelectedVariant} />}
              {hasRoute && (
                <button type="button" className="vt-btn vt-primary" style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}
                        onClick={() => setSuggestOpen(true)}>Suggest improvement…</button>
              )}
            </section>

            <section className="rt-sec">
              <h2 className="rt-h">Test edits</h2>
              <div className="rt-row">
                {[['blocked', 'Blocked', 'B'], ['non-accessible', 'Non-accessible', 'N'], ['shaft', 'Disable shaft', 'D']].map(([t, label, key]) => (
                  <button key={t} type="button" className={`vt-btn vt-small${tool === t ? ' vt-primary' : ''}`} aria-pressed={tool === t}
                          title={`${label} (${key})`} onClick={() => setTool((cur) => (cur === t ? null : t))}>{label}</button>
                ))}
              </div>
              <div className="rt-row">
                <button type="button" className="vt-btn vt-small" onClick={undo} disabled={!hist.past.length} title="Undo (Cmd/Ctrl+Z)">↶ Undo</button>
                <button type="button" className="vt-btn vt-small" onClick={redo} disabled={!hist.future.length} title="Redo (Shift+Cmd/Ctrl+Z)">↷ Redo</button>
              </div>
              <p className="vt-hint">Non-accessible only changes the Accessible variant; Blocked refuses every edge touching the node; a disabled shaft keeps its landings walkable.</p>
              {edits.length ? (
                <ul className="rt-edits" aria-label="Edits">
                  {edits.map((e, i) => (
                    <li key={i} className={selectedEdit === i ? 'rt-edit-sel' : ''}>
                      <button type="button" className="rt-editbtn" onClick={() => setSelectedEdit((s) => (s === i ? null : i))}>
                        <span className="rt-dot" style={{ background: e.kind === 'shaft' ? NODE_COLORS.shaftOff : NODE_COLORS[e.type] }} aria-hidden="true" />
                        {editLabel(e, mapData)}
                      </button>
                      <button type="button" className="vt-btn vt-ghost vt-small" aria-label="Remove edit" onClick={() => removeEdit(i)}>✕</button>
                    </li>
                  ))}
                </ul>
              ) : <p className="vt-hint">No edits — this is the route as students get it.</p>}
            </section>

            {hasRoute && (
              <section className="rt-sec">
                <h2 className="rt-h">Directions · {VARIANT_BY_KEY[shownVariant].label}</h2>
                {shownRoute ? (
                  directions.length ? (
                    <ol className="rt-dirs">
                      {directions.map((d) => <li key={d.id}>{d.icon ? <span aria-hidden="true">{d.icon} </span> : null}{d.text}{d.chip ? <span className="vt-pill vt-pill-dim rt-chip">{d.chip}</span> : null}</li>)}
                    </ol>
                  ) : <p className="vt-hint">Nothing to say — a straight walk.</p>
                ) : <p className="vt-hint">No route for this variant.</p>}
              </section>
            )}

            {mapData && <DataHealth mapData={mapData} rooms={disconnectedRooms} onRoom={goToRoom} />}
            {admin && <p className="vt-hint">You are the operator: suggestions you send land in your own /admin → Volunteer Admin Dashboard.</p>}
          </div>
        )}

        {tab === 'history' && (
          <HistoryTab entries={history} err={historyErr} mapData={mapData} onLoad={loadParams} onDelete={deleteHistory} onClear={clearHistory} />
        )}
        {tab === 'suggest' && (
          <MySuggestions items={mine} err={mineErr} mapData={mapData} token={token} onExpired={onExpired}
                         onReload={loadMine} onLoad={(s) => loadParams(s.original, s.edits || [])} />
        )}
      </aside>

      <div className="rt-map">
        <div ref={mapDiv} className="rt-canvas" aria-label="Campus map" />
        {!isLoaded && MAPS_KEY && <div className="rt-mapnote">Loading Google Maps…</div>}
        {tool && <div className="rt-banner">{TOOL_HINT[tool]} · <b>Esc</b> cancels</div>}
        <div className="rt-floors" role="group" aria-label="Floor">
          {FLOORS.map((f) => (
            <button key={f} type="button" aria-pressed={floor === f} onClick={() => setFloor(f)}
                    title={f === 1 ? 'Floor 1 and the tunnels' : `Floor ${f}`}>{f}</button>
          ))}
          <button type="button" className="rt-rooms" aria-pressed={showRooms} onClick={() => setShowRooms((v) => !v)} title="Show room numbers (R)">#</button>
        </div>
        <Legend />
        {inspect && <Inspector node={inspect} mapData={mapData} edits={edits} onClose={() => setInspect(null)}
                               onStart={() => { setStart(nodeEndpoint(inspect)); setInspect(null); }}
                               onEnd={() => { setEnd(nodeEndpoint(inspect)); setInspect(null); }}
                               addEdit={addEdit} say={say} />}
      </div>

      {helpOpen && <HelpOverlay onClose={() => setHelpOpen(false)} />}
      {suggestOpen && hasRoute && (
        <SuggestDialog mapData={mapData} start={start} end={end} variants={variants} edits={edits} results={results}
                       token={token} onExpired={onExpired} onClose={() => setSuggestOpen(false)}
                       onSent={(msg) => { setSuggestOpen(false); say(msg); loadMine(); setTab('suggest'); }} />
      )}
    </div>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────────

function Analytics({ results, ranKeys, shown, onPick }) {
  const rows = [
    ['Metres', (m) => `${m.meters} m`],
    ['Floor changes', (m) => m.floorChanges],
    ['Minutes', (m) => `${m.mins} min`],
    ['Outdoor', (m) => `${m.outdoorMeters} m`],
    ['Tunnel', (m) => `${m.tunnelMeters} m`],
  ];
  if (ranKeys.length <= 1) {
    const m = results.metrics.base;
    if (!m) return <div className="vt-err" style={{ marginBottom: 0 }}>No route — the Normal route could not connect these two points.</div>;
    return (
      <div className="vt-stats">
        <span><b>{m.meters} m</b>distance</span>
        <span><b>{m.floorChanges}</b>floor changes</span>
        <span><b>{m.mins} min</b>walk</span>
        <span><b>{m.outdoorMeters} m</b>outdoors</span>
        {m.tunnelMeters ? <span><b>{m.tunnelMeters} m</b>tunnels</span> : null}
      </div>
    );
  }
  return (
    <div className="rt-tablewrap">
    <table className="rt-table">
      <thead>
        <tr>
          <th />
          {ranKeys.map((k) => (
            <th key={k}>
              <button type="button" className="rt-colbtn" aria-pressed={shown === k} onClick={() => onPick(k)} title="Show directions for this variant">
                <span className={`rt-swatch rt-swatch-${VARIANT_BY_KEY[k].dash}`} style={{ color: VARIANT_BY_KEY[k].color }} aria-hidden="true" />
                {VARIANT_BY_KEY[k].short}
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, fn]) => (
          <tr key={label}>
            <th>{label}</th>
            {ranKeys.map((k) => <td key={k}>{results.metrics[k] ? fn(results.metrics[k]) : <span className="rt-nr">No route</span>}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function Legend() {
  return (
    <div className="rt-legend" aria-label="Legend">
      <div className="rt-legend-row">
        {VARIANTS.map((v) => <span key={v.key}><span className={`rt-swatch rt-swatch-${v.dash}`} style={{ color: v.color }} aria-hidden="true" />{v.label}</span>)}
      </div>
      <div className="rt-legend-row">
        {LEGEND.map((l) => (
          <span key={l.key}>
            <span className={`rt-dot${l.hatched ? ' rt-dot-hatch' : ''}`} style={{ background: l.hatched ? undefined : NODE_COLORS[l.key] }} aria-hidden="true">{l.mark || ''}</span>
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Inspector({ node, mapData, edits, onClose, onStart, onEnd, addEdit, say }) {
  const degree = mapData?.adjacency?.get(node.id)?.length ?? 0;
  const t = effectiveType(node, edits);
  const copy = async () => {
    try { await navigator.clipboard.writeText(node.id); say('Node id copied.'); } catch { say(node.id); }
  };
  const shaft = node.type === 'stair' || node.type === 'elevator';
  return (
    <div className="rt-inspect vt-card" role="dialog" aria-label="Node">
      <div className="rt-inspect-head">
        <b>{nodeLabel(node)}</b>
        <button type="button" className="vt-btn vt-ghost vt-small" aria-label="Close" onClick={onClose}>✕</button>
      </div>
      <dl className="rt-dl">
        <dt>id</dt><dd><code>{node.id}</code> <button type="button" className="vt-btn vt-ghost vt-small" onClick={copy}>Copy</button></dd>
        <dt>type</dt><dd>{node.type}{t !== node.type ? <> → <b>{t === 'shaftOff' ? 'disabled shaft' : t}</b></> : null}</dd>
        <dt>floor</dt><dd>{node.floor === 0 ? '0 (outdoors)' : node.floor === -1 ? '−1 (tunnels)' : node.floor}</dd>
        <dt>building</dt><dd>{buildingName(mapData, node.building) || '—'}</dd>
        <dt>degree</dt><dd>{degree}</dd>
      </dl>
      <div className="rt-row">
        <button type="button" className="vt-btn vt-small" onClick={onStart}>Set Start</button>
        <button type="button" className="vt-btn vt-small" onClick={onEnd}>Set End</button>
        {editCovering(edits, node.id) < 0 && (
          <>
            <button type="button" className="vt-btn vt-small" onClick={() => { addEdit(nodeEdit(node, 'blocked')); onClose(); }}>Block</button>
            <button type="button" className="vt-btn vt-small" onClick={() => { addEdit(nodeEdit(node, 'non-accessible')); onClose(); }}>Non-acc.</button>
            {shaft && <button type="button" className="vt-btn vt-small" onClick={() => { const s = resolveShaft(mapData, node.id); if (s) { addEdit(shaftEdit(node, s)); onClose(); } else say('No shaft links from this node.'); }}>Disable shaft</button>}
          </>
        )}
      </div>
    </div>
  );
}

function DataHealth({ mapData, rooms, onRoom }) {
  const [open, setOpen] = useState(false);
  const warnings = mapData.warnings || [];
  return (
    <section className="rt-sec">
      <h2 className="rt-h">Data health</h2>
      <p className="vt-hint">{warnings.length} warning{warnings.length === 1 ? '' : 's'} from the graph build · {rooms.length} room{rooms.length === 1 ? '' : 's'} with no connected door.</p>
      <button type="button" className="vt-btn vt-small" onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Hide' : 'Show'} details</button>
      {open && (
        <div className="rt-health">
          {rooms.length > 0 && (
            <ul className="rt-health-list" aria-label="Disconnected rooms">
              {rooms.slice(0, 150).map((e) => (
                <li key={`${e.building}::${e.roomNumber}`}>
                  <button type="button" className="rt-link" onClick={() => onRoom(e)}>{e.buildingName} {e.roomNumber}</button>
                </li>
              ))}
              {rooms.length > 150 && <li className="vt-hint">…and {rooms.length - 150} more</li>}
            </ul>
          )}
          {warnings.length > 0 && (
            <ul className="rt-health-list rt-warnings" aria-label="Warnings">
              {warnings.slice(0, 200).map((w, i) => <li key={i}>{w}</li>)}
              {warnings.length > 200 && <li className="vt-hint">…and {warnings.length - 200} more</li>}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function HistoryTab({ entries, err, mapData, onLoad, onDelete, onClear }) {
  const groups = useMemo(() => groupByDay(entries), [entries]);
  return (
    <div>
      <div className="rt-row" style={{ justifyContent: 'space-between' }}>
        <h2 className="rt-h" style={{ margin: 0 }}>Your tests</h2>
        {entries.length > 0 && <button type="button" className="vt-btn vt-danger vt-small" onClick={onClear}>Clear all</button>}
      </div>
      {err && <div className="vt-err">{err}</div>}
      {!entries.length && !err && <p className="vt-hint">Every route you run is kept here for the day, newest first. Click one to load it exactly.</p>}
      {groups.map((g) => (
        <section key={g.day} className="rt-sec">
          <h3 className="rt-day">{g.label}</h3>
          <ul className="rt-hist">
            {g.entries.map((e) => {
              const p = e.params || {};
              const base = e.metrics?.base;
              return (
                <li key={e.id}>
                  <button type="button" className="rt-histbtn" onClick={() => onLoad(p)} title="Load this test">
                    <div className="rt-histroute">{endpointLabel(mapData, p.start)} → {endpointLabel(mapData, p.end)}</div>
                    <div className="vt-meta">
                      {variantsText(p.variants)} · {p.edits?.length || 0} edit{p.edits?.length === 1 ? '' : 's'} · {base === undefined ? '—' : metricsSummary(base)} · {ago(e.at)}
                    </div>
                  </button>
                  <button type="button" className="vt-btn vt-ghost vt-small" aria-label="Delete" onClick={() => onDelete(e.id)}>✕</button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

const STATUS_PILL = {
  pending: ['vt-pill-new', 'Pending'],
  'needs-detail': ['vt-pill-warn', 'Needs detail'],
  closed: ['vt-pill-dim', 'Closed'],
};

/**
 * "Screenshot ↗" for a suggestion. In production the stored URL is a public
 * Blob URL and a plain link is enough; in dev it is the Bearer-gated
 * `?part=file` path, which a tab cannot open on its own, so the click fetches
 * it with the token and opens the bytes from an object URL instead.
 */
function ScreenshotLink({ src, token }) {
  const relative = typeof src === 'string' && src.startsWith('/api/');
  const [busy, setBusy] = useState(false);
  const open = async (e) => {
    if (!relative) return; // let the anchor do its job
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}${src}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(String(r.status));
      const url = URL.createObjectURL(await r.blob());
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      window.alert('Could not load the screenshot.');
    } finally { setBusy(false); }
  };
  return (
    <a className="vt-btn vt-small" href={relative ? `${API_BASE}${src}` : src} target="_blank" rel="noopener noreferrer" onClick={open}>
      {busy ? 'Loading…' : 'Screenshot ↗'}
    </a>
  );
}

function MySuggestions({ items, err, mapData, token, onExpired, onReload, onLoad }) {
  const [reply, setReply] = useState({});
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const send = async (id) => {
    const text = (reply[id] || '').trim();
    if (!text) return;
    setBusy(id); setMsg('');
    const r = await api('/api/route-tests', { method: 'POST', token, onExpired, body: { op: 'detail', id, text } });
    setBusy('');
    if (r.ok) { setReply((m) => ({ ...m, [id]: '' })); onReload(); }
    else setMsg(r.body.error || 'Could not send the reply.');
  };
  return (
    <div>
      <h2 className="rt-h">My suggestions</h2>
      {err && <div className="vt-err">{err}</div>}
      {msg && <div className="vt-err">{msg}</div>}
      {!items.length && !err && <p className="vt-hint">Nothing sent yet. Run a route, make an edit that improves it, and press <b>Suggest improvement</b>.</p>}
      <div className="vt-list">
        {items.map((s) => {
          const [cls, label] = STATUS_PILL[s.status] || STATUS_PILL.pending;
          const o = s.original || {};
          return (
            <div key={s.id} className="vt-row">
              <div className="vt-rowhead">
                <div className="vt-grow">
                  <div className="vt-title">{endpointLabel(mapData, o.start)} → {endpointLabel(mapData, o.end)}</div>
                  <div className="vt-meta">{variantsText(o.variants)} · {s.edits?.length || 0} edit{s.edits?.length === 1 ? '' : 's'} · {ago(s.at)}</div>
                </div>
                <span className={`vt-pill ${cls}`}>{label}</span>
              </div>
              <div className="vt-meta">Before: {metricsSummary(s.before?.base)} → After: {metricsSummary(s.after?.base)}</div>
              {(s.edits || []).length > 0 && (
                <ul className="rt-edits rt-edits-plain">{s.edits.map((e, i) => <li key={i}>{editLabel(e, mapData)}</li>)}</ul>
              )}
              {(s.thread || []).length > 0 && (
                <div className="rt-thread">
                  {s.thread.map((t, i) => (
                    <div key={i} className={`rt-msg rt-msg-${t.role}`}>
                      <span className="vt-meta">{t.role === 'operator' ? 'UMaps' : 'You'} · {ago(t.at)}</span>
                      <div className="vt-issue-msg">{t.text}</div>
                    </div>
                  ))}
                </div>
              )}
              {s.status === 'needs-detail' && (
                <div className="rt-reply">
                  <textarea className="vt-textarea" placeholder="Add the detail UMaps asked for…" value={reply[s.id] || ''}
                            onChange={(e) => setReply((m) => ({ ...m, [s.id]: e.target.value }))} />
                  <button type="button" className="vt-btn vt-primary vt-small" disabled={busy === s.id || !(reply[s.id] || '').trim()} onClick={() => send(s.id)}>
                    {busy === s.id ? 'Sending…' : 'Reply'}
                  </button>
                </div>
              )}
              <div className="rt-row" style={{ marginTop: 8 }}>
                <button type="button" className="vt-btn vt-small" onClick={() => onLoad(s)}>Load in tester</button>
                {s.screenshot && <ScreenshotLink src={s.screenshot} token={token} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SuggestDialog({ mapData, start, end, variants, edits, results, token, onExpired, onClose, onSent }) {
  const [comment, setComment] = useState('');
  const [shot, setShot] = useState(null); // { name, dataUrl }
  const [shotErr, setShotErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ran = VARIANTS.filter((v) => v.key in (results.metrics || {}));

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    setShotErr('');
    if (!f) { setShot(null); return; }
    if (!SHOT_TYPES.includes(f.type)) { setShotErr('PNG, JPEG or WebP only.'); setShot(null); return; }
    if (f.size > SHOT_MAX) { setShotErr('Keep the screenshot under 4 MB.'); setShot(null); return; }
    const dataUrl = await readFileAsDataUrl(f);
    if (!dataUrl) { setShotErr('Could not read that file.'); setShot(null); return; }
    setShot({ name: f.name, dataUrl });
  };
  const submit = async (ev) => {
    ev.preventDefault();
    if (busy) return;
    if (!edits.length && !comment.trim()) { setErr('Add an edit or a comment — an empty suggestion says nothing.'); return; }
    setBusy(true); setErr('');
    const r = await api('/api/route-tests', {
      method: 'POST', token, onExpired,
      body: {
        op: 'suggest',
        original: { start, end, variants, edits: [] },
        edits, before: results.before, after: results.metrics,
        comment: comment.trim(),
        ...(shot ? { screenshot: { dataUrl: shot.dataUrl } } : {}),
      },
    });
    setBusy(false);
    if (!r.ok) { setErr(r.body.error || 'Could not send the suggestion.'); return; }
    onSent(r.body.screenshotError ? `Suggestion sent — but the screenshot was not kept: ${r.body.screenshotError}` : 'Suggestion sent. Thank you!');
  };

  return (
    <div className="rt-modal" role="dialog" aria-modal="true" aria-label="Suggest improvement" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="rt-modal-card vt-card vt-pad" onSubmit={submit}>
        <div className="rt-inspect-head">
          <h2 className="vt-h1" style={{ margin: 0, fontSize: 18 }}>Suggest improvement</h2>
          <button type="button" className="vt-btn vt-ghost vt-small" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        {err && <div className="vt-err">{err}</div>}
        <div className="vt-field">
          <span className="vt-label">Route</span>
          <div className="vt-route">{endpointLabel(mapData, start)} → {endpointLabel(mapData, end)}</div>
          <div className="vt-meta">{variantsText(variants)}</div>
        </div>
        <div className="vt-field">
          <span className="vt-label">What changed</span>
          {edits.length ? (
            <ul className="rt-diff">
              {edits.map((e, i) => <li key={i}><span className="rt-plus">+</span> {editLabel(e, mapData)}</li>)}
            </ul>
          ) : <div className="vt-hint">No edits — the original route is what this is about.</div>}
        </div>
        <div className="vt-field">
          <span className="vt-label">Before → after</span>
          {ran.map((v) => (
            <div key={v.key} className="rt-ba">
              <span className={`rt-swatch rt-swatch-${v.dash}`} style={{ color: v.color }} aria-hidden="true" />
              <b>{v.label}:</b> Before: {metricsSummary(results.before[v.key])} → After: {metricsSummary(results.metrics[v.key])}
            </div>
          ))}
        </div>
        <div className="vt-field">
          <label className="vt-label" htmlFor="rt-comment">Comment (optional)</label>
          <textarea id="rt-comment" className="vt-textarea" maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder="What is wrong with the route today, and what your edit fixes." />
        </div>
        <div className="vt-field">
          <label className="vt-label" htmlFor="rt-shot">Screenshot (optional · PNG, JPEG or WebP, up to 4 MB)</label>
          <input id="rt-shot" type="file" accept="image/png,image/jpeg,image/webp" onChange={pickFile} />
          {shot && <div className="vt-hint">{shot.name}</div>}
          {shotErr && <div className="vt-err" style={{ marginTop: 6 }}>{shotErr}</div>}
        </div>
        <div className="vt-actions">
          <button type="button" className="vt-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="vt-btn vt-primary" disabled={busy}>{busy ? 'Sending…' : 'Send suggestion'}</button>
        </div>
      </form>
    </div>
  );
}

const SHORTCUTS = [
  ['S', 'Arm the Start pick tool'], ['E', 'Arm the End pick tool'], ['X', 'Swap Start and End'],
  ['Esc', 'Cancel the tool / close a dialog'],
  ['Enter', 'Pick the hovered node or room (tool armed); otherwise open Suggest improvement'],
  ['Backspace / Delete', 'Remove the selected edit, else clear End, else clear Start'],
  ['Shift + click', 'Set a node as End without arming'], ['Alt + click', 'Set a node as Start without arming'],
  ['B', 'Blocked tool'], ['N', 'Non-accessible tool'], ['D', 'Disable shaft tool'],
  ['Cmd/Ctrl + Z', 'Undo an edit'], ['Shift + Cmd/Ctrl + Z', 'Redo'],
  ['1 – 6', 'Floor (1 also shows the tunnels)'], ['R', 'Show room numbers'],
  ['A', 'Toggle Accessible'], ['W', 'Toggle Bad weather (tunnels)'], ['Q', 'Toggle Stairs only'],
  ['Z', 'Fit the map to the route'], ['H', 'History tab'], ['?', 'This help'],
];

function HelpOverlay({ onClose }) {
  return (
    <div className="rt-modal" role="dialog" aria-modal="true" aria-label="Keyboard shortcuts" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rt-modal-card vt-card vt-pad">
        <div className="rt-inspect-head">
          <h2 className="vt-h1" style={{ margin: 0, fontSize: 18 }}>Keyboard shortcuts</h2>
          <button type="button" className="vt-btn vt-ghost vt-small" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <dl className="rt-keys">
          {SHORTCUTS.map(([k, what]) => <div key={k}><dt><kbd>{k}</kbd></dt><dd>{what}</dd></div>)}
        </dl>
        <p className="vt-hint">Shortcuts are ignored while you are typing in a box.</p>
      </div>
    </div>
  );
}

const CSS = `
.rt { display:grid; grid-template-columns:340px 1fr; height:100%; min-height:0; background:var(--page); }
.rt-panel { overflow-y:auto; min-height:0; padding:12px 14px 40px; border-right:1px solid var(--line); background:#fff; }
.rt-tabs { position:sticky; top:0; background:#fff; padding:2px 0 8px; margin:0 0 8px; z-index:2; align-items:center; }
.rt-help { margin-left:auto; font-weight:800; }
.rt-sec { margin:0 0 16px; }
.rt-h { font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:.05em; color:var(--dim); margin:0 0 8px; }
.rt-day { font-size:13px; font-weight:750; margin:0 0 6px; }
.rt-row { display:flex; gap:6px; flex-wrap:wrap; align-items:center; margin:6px 0; }
.rt-ep { display:flex; gap:8px; align-items:center; margin:0 0 6px; }
.rt-ep .vt-btn { width:84px; justify-content:flex-start; }
.rt-eplabel { flex:1; min-width:0; font-size:13px; font-weight:650; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.rt-faint { color:var(--faint); font-weight:400; }
.rt-dot { display:inline-flex; align-items:center; justify-content:center; width:12px; height:12px; border-radius:50%;
  border:1.5px solid #fff; box-shadow:0 0 0 1px rgba(0,0,0,.18); font-size:8px; color:#fff; font-weight:800; line-height:1; flex:none; }
.rt-dot-hatch { background:repeating-linear-gradient(45deg,#607d8b 0 2px,#cfd8dc 2px 4px); }
.rt-swatch { display:inline-block; width:22px; height:0; border-top:4px solid currentColor; margin-right:6px; vertical-align:middle; border-radius:2px; }
.rt-swatch-dashed { border-top-style:dashed; }
.rt-swatch-dotted { border-top-style:dotted; }
.rt-variants { display:flex; flex-direction:column; gap:4px; }
.rt-variant { display:flex; align-items:center; gap:8px; font-size:13.5px; cursor:pointer; padding:3px 0; }
.rt-variant-fixed { color:var(--dim); cursor:default; }
.rt-variant input { margin:0; }
.rt-tablewrap { overflow-x:auto; margin:0 -4px; }
.rt-table { width:100%; border-collapse:collapse; font-size:12.5px; }
.rt-table th, .rt-table td { padding:5px 4px; white-space:nowrap; border-bottom:1px solid #f0f1f3; text-align:left; font-variant-numeric:tabular-nums; }
.rt-table tbody th { font-weight:650; color:var(--dim); width:1%; white-space:nowrap; }
.rt-colbtn { font:inherit; font-size:12px; font-weight:700; border:1px solid transparent; background:none; border-radius:6px; padding:3px 5px; cursor:pointer; color:var(--ink); white-space:nowrap; }
.rt-colbtn[aria-pressed="true"] { border-color:var(--line); background:var(--page); }
.rt-nr { color:var(--bad); font-weight:700; }
.rt-edits { list-style:none; margin:8px 0 0; padding:0; display:flex; flex-direction:column; gap:3px; }
.rt-edits li { display:flex; align-items:center; gap:4px; border:1px solid var(--line); border-radius:8px; padding:2px 4px 2px 6px; font-size:13px; }
.rt-edits li.rt-edit-sel { border-color:var(--accent); background:var(--accentSoft); }
.rt-edits-plain li { border:none; padding:1px 0; font-size:12.5px; color:var(--dim); }
.rt-editbtn { flex:1; min-width:0; text-align:left; font:inherit; background:none; border:none; padding:4px 0; cursor:pointer; color:var(--ink);
  display:flex; align-items:center; gap:7px; overflow:hidden; }
.rt-dirs { margin:0; padding-left:20px; font-size:13px; line-height:1.5; }
.rt-dirs li { margin:0 0 3px; }
.rt-chip { margin-left:6px; font-size:10px; }
.rt-health { margin-top:8px; }
.rt-health-list { margin:6px 0 0; padding-left:18px; font-size:12.5px; line-height:1.45; max-height:220px; overflow-y:auto; }
.rt-warnings { color:var(--warn); }
.rt-link { font:inherit; background:none; border:none; padding:0; color:var(--accent); cursor:pointer; text-decoration:underline; }
.rt-hist { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:4px; }
.rt-hist li { display:flex; gap:4px; align-items:flex-start; }
.rt-histbtn { flex:1; min-width:0; text-align:left; font:inherit; background:#fff; border:1px solid var(--line); border-radius:10px; padding:8px 10px; cursor:pointer; color:var(--ink); }
.rt-histbtn:hover { border-color:var(--accent); }
.rt-histroute { font-weight:650; font-size:13px; overflow-wrap:anywhere; }
.rt-thread { margin-top:8px; display:flex; flex-direction:column; gap:6px; }
.rt-msg { border-left:3px solid var(--line); padding:2px 0 2px 10px; font-size:13px; }
.rt-msg-operator { border-left-color:var(--accent); }
.rt-reply { margin-top:8px; display:flex; flex-direction:column; gap:6px; align-items:flex-end; }
.rt-map { position:relative; min-height:0; background:#dfe3e8; }
.rt-canvas { position:absolute; inset:0; }
.rt-mapnote { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:var(--dim); pointer-events:none; }
.rt-banner { position:absolute; top:10px; left:50%; transform:translateX(-50%); background:rgba(27,30,35,.9); color:#fff; font-size:13px;
  padding:6px 12px; border-radius:999px; pointer-events:none; z-index:3; white-space:nowrap; max-width:90%; overflow:hidden; text-overflow:ellipsis; }
.rt-floors { position:absolute; right:10px; top:50%; transform:translateY(-50%); display:flex; flex-direction:column; gap:4px; z-index:3; }
.rt-floors button { width:36px; height:34px; font:inherit; font-weight:800; border:1px solid var(--line); background:rgba(255,255,255,.95); border-radius:8px; cursor:pointer; color:var(--ink); box-shadow:0 1px 4px rgba(0,0,0,.15); }
.rt-floors button[aria-pressed="true"] { background:var(--accent); color:#fff; border-color:var(--accent); }
.rt-floors .rt-rooms { margin-top:6px; }
.rt-legend { position:absolute; left:10px; bottom:10px; z-index:3; background:rgba(255,255,255,.94); border:1px solid var(--line); border-radius:10px;
  padding:7px 10px; font-size:11.5px; display:flex; flex-direction:column; gap:5px; max-width:min(460px, calc(100% - 20px)); box-shadow:0 1px 6px rgba(0,0,0,.12); }
.rt-legend-row { display:flex; flex-wrap:wrap; gap:4px 12px; align-items:center; }
.rt-legend-row > span { display:inline-flex; align-items:center; gap:5px; white-space:nowrap; }
.rt-inspect { position:absolute; right:56px; bottom:10px; z-index:4; width:min(300px, calc(100% - 70px)); padding:10px 12px; font-size:13px; box-shadow:0 4px 18px rgba(0,0,0,.18); }
.rt-inspect-head { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:6px; }
.rt-dl { display:grid; grid-template-columns:auto 1fr; gap:3px 10px; margin:0 0 6px; }
.rt-dl dt { color:var(--dim); font-size:12px; }
.rt-dl dd { margin:0; overflow-wrap:anywhere; }
.rt-dl code { font-size:12px; }
.rt-modal { position:fixed; inset:0; background:rgba(27,30,35,.45); z-index:60; display:flex; align-items:center; justify-content:center; padding:16px; }
.rt-modal-card { width:min(560px, 100%); max-height:92vh; overflow-y:auto; }
.rt-diff { margin:0; padding:0; list-style:none; font-size:13px; }
.rt-diff li { padding:3px 8px; background:var(--goodSoft); border-radius:6px; margin-bottom:3px; }
.rt-plus { font-weight:800; color:var(--good); }
.rt-ba { font-size:13px; margin:0 0 4px; display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
.rt-keys { margin:0 0 10px; display:grid; gap:5px; }
.rt-keys > div { display:grid; grid-template-columns:150px 1fr; gap:10px; align-items:baseline; font-size:13px; }
.rt-keys dt, .rt-keys dd { margin:0; }
.rt-keys kbd { font-family:inherit; font-size:12px; font-weight:700; background:var(--page); border:1px solid var(--line); border-bottom-width:2px; border-radius:6px; padding:1px 6px; }
@media (max-width: 820px) {
  .rt { grid-template-columns:1fr; grid-template-rows:56vh auto; height:auto; }
  .rt-map { order:-1; min-height:56vh; }
  .rt-panel { border-right:none; border-top:1px solid var(--line); overflow:visible; }
  .rt-legend { max-width:calc(100% - 70px); font-size:10.5px; }
  .rt-inspect { right:10px; bottom:auto; top:50px; width:min(300px, calc(100% - 70px)); }
  .rt-keys > div { grid-template-columns:110px 1fr; }
}
`;
